import { Link } from 'react-router';
import { Artwork } from '@/components/Artwork';
import { buttonClasses } from '@/components/Button';
import { Icon } from '@/components/Icon';
import type { IconName } from '@/components/Icon';
import { SkipLink } from '@/shells/nav';
import { CantScanCard } from './CantScan';

const STEPS: { icon: IconName; title: string; text: string }[] = [
  {
    icon: 'pin',
    title: 'Scan the code',
    text: "Point your phone's camera at the QR code on your table or seat.",
  },
  {
    icon: 'menu',
    title: 'Order when you are ready',
    text: 'Browse the menu, choose your extras and send your order straight to the kitchen.',
  },
  {
    icon: 'bill',
    title: 'Follow your bill',
    text: 'Your running bill is always on your phone. Settle up with staff when you are done.',
  },
];

/**
 * `/` — the bare address, with no table behind it. It cannot know the venue, so it is ServeFlow's own
 * welcome: how ordering works, what to do if the code will not scan, and the way in for staff. Guests
 * who scan a code never see it; they land on `/q/:qrToken`.
 */
export function LandingPage() {
  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <SkipLink />
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex min-h-14 max-w-6xl items-center justify-between gap-3 px-4 md:px-6">
          <span className="flex items-center gap-2 text-base font-bold text-ink">
            <span
              aria-hidden="true"
              className="grid size-7 place-items-center rounded-control bg-primary text-sm text-white"
            >
              S
            </span>
            ServeFlow
          </span>
          <Link to="/login" className={buttonClasses({ variant: 'tertiary' })}>
            Staff sign in
          </Link>
        </div>
      </header>

      <main id="main" tabIndex={-1} className="flex-1">
        <section
          aria-labelledby="landing-title"
          className="mx-auto grid max-w-6xl items-center gap-8 px-4 pt-8 pb-12 md:px-6 lg:grid-cols-2 lg:gap-16 lg:pt-16 lg:pb-20"
        >
          <div className="flex flex-col gap-5">
            <p className="text-xs font-semibold tracking-wide text-primary uppercase">
              Order from your seat
            </p>
            <h1
              id="landing-title"
              className="text-3xl leading-tight font-bold text-ink md:text-5xl md:leading-tight"
            >
              Scan the QR code at your table or seat
            </h1>
            <p className="max-w-lg text-base text-muted md:text-lg">
              See the menu, place your order and keep an eye on your bill, all from your phone. No
              app to install and no account to create.
            </p>
            <CantScanCard />
          </div>
          <Artwork
            name="table"
            className="mx-auto max-w-64 sm:max-w-sm md:max-w-md lg:max-w-none"
          />
        </section>

        <section aria-labelledby="how-title" className="border-t border-line bg-white">
          <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-12 md:px-6 lg:py-16">
            <h2 id="how-title" className="text-2xl font-bold text-ink">
              How it works
            </h2>
            <ol className="grid gap-4 md:grid-cols-3">
              {STEPS.map((step, index) => (
                <li
                  key={step.title}
                  className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-5"
                >
                  <span className="flex items-center gap-3">
                    <span className="grid size-10 place-items-center rounded-full bg-primary text-white">
                      <Icon name={step.icon} size={20} />
                    </span>
                    <span className="text-sm font-semibold text-primary">Step {index + 1}</span>
                  </span>
                  <h3 className="text-lg font-semibold text-ink">{step.title}</h3>
                  <p className="text-sm text-muted">{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>
      </main>

      <footer className="border-t border-line bg-canvas">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-4 py-5 text-sm text-muted md:px-6">
          <span>ServeFlow · Dine-in ordering</span>
          <Link to="/login" className="font-semibold text-primary hover:underline">
            Staff sign in
          </Link>
        </div>
      </footer>
    </div>
  );
}
